import {Component, ComponentRef, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';

import {TranslateService} from '@ngx-translate/core';

import {merge, Observable} from 'rxjs';
import {map, takeUntil, tap} from 'rxjs/operators';

import {ESSService} from '../+ess/employee-self.service';
import {GP_DATE_FORMAT, GP_TIMEZONE_LIST, NUMBER_DECIMAL_SYMBOLS, NUMBER_SEPARATOR_SYMBOLS} from '../app.constants';
import {Destroyable} from '../destroyable.component';
import {ApiService} from '../shared/api/api.service';
import {Employee} from '../shared/employees/employees.model';
import {I18nManager} from '../shared/i18n/i18n.manager';
import {LocalizationService} from '../shared/localization/localization.service';
import {ModalService} from '../shared/modal/modal.service';
import {User, UserService} from '../shared/user';
import {UserAvatarCropperComponent} from '../shared/user/user-avatar-cropper.component';
import {getDefaultLocalTimezone} from '../shared/utils/date';

/**
 * Account preferences shown in a right-side drawer (replaces the old navbar
 * dropdown + "My profile" modal). Contains the same preferences as the modal
 * (language, timezone, number/date format, auto-save, email) plus Logout /
 * Change password actions.
 */
@Component({
  selector: 'gp-profile-drawer',
  templateUrl: './profile-drawer.component.html',
  styleUrls: ['./profile-drawer.component.scss']
})
export class ProfileDrawerComponent extends Destroyable implements OnInit {

  @Input() public open = false;
  @Output() public openChange = new EventEmitter<boolean>();
  @Output() public logout = new EventEmitter<void>();
  @Output() public changePassword = new EventEmitter<void>();
  @Input() public canChangePassword = true;

  public user: User;
  public employee: Employee;
  public saving = false;

  /** Single reactive form holding every editable preference. */
  public form: UntypedFormGroup;

  // Option lists rendered in the template's dropdowns.
  public languages: { [key: string]: string } = {};
  public timezoneNames: any;
  public numberDecimalSymbols: string[];
  public numberSeparatorSymbols: string[];
  public dateFormats: string[];

  // Snapshot of the form values when the drawer was opened. Used to diff
  // changes (Save button + payload) and to restore the form on cancel.
  private pristineValue: ProfilePreferences;

  constructor(private userService: UserService,
              private i18nManager: I18nManager,
              private localization: LocalizationService,
              private modalService: ModalService,
              private essService: ESSService,
              private translate: TranslateService,
              private fb: UntypedFormBuilder,
              public api: ApiService) {
    super();
    this.languages = this.i18nManager.availableLanguages;
    this.numberSeparatorSymbols = NUMBER_SEPARATOR_SYMBOLS;
    this.numberDecimalSymbols = NUMBER_DECIMAL_SYMBOLS;
    this.dateFormats = GP_DATE_FORMAT;

    this.form = this.fb.group({
      language: [this.i18nManager.currentLanguage],
      timezone: [''],
      numberFormat: [''],
      dateFormat: [''],
      email: ['', Validators.email],
      sendEmail: [false],
      autoSave: [false]
    });
  }

  public ngOnInit(): void {
    merge(
      this.userService.getUser().pipe(map((user: User) => this.user = user)),
      this.translate.onLangChange
    )
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => {
        if (this.user) {
          this.timezoneNames = GP_TIMEZONE_LIST;
          const preferences: ProfilePreferences = {
            language: this.i18nManager.currentLanguage,
            timezone: this.user.timezone || getDefaultLocalTimezone(),
            numberFormat: this.localization.numberFormat.decimal + this.localization.numberFormat.separator,
            dateFormat: this.localization.dateFormat,
            email: this.user.email,
            sendEmail: !!this.user.sendEmail,
            autoSave: !!this.user.autoSave
          };
          this.form.reset(preferences, {emitEvent: false});
          this.pristineValue = preferences;
        }
      });

    this.essService.currentUser
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => this.employee = this.essService.employee);

    this.form.get('email').valueChanges
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((value: string) => {
        // "Send email" only stays on while the address is unchanged.
        const sameEmail = !!this.pristineValue && value === this.pristineValue.email;
        this.form.get('sendEmail').setValue(sameEmail ? this.pristineValue.sendEmail : false, {emitEvent: false});
      });
  }

  public initials(): string {
    return (this.user?.firstName?.charAt(0) || '') + (this.user?.lastName?.charAt(0) || '');
  }


  public photoSelected(event: any): void {
    if (event.target.files.length > 0) {
      this.modalService.openModalWithName('UserAvatarCropper')
        .then((modelRef: ComponentRef<UserAvatarCropperComponent>) => {
          modelRef.instance.employee = this.essService.employee;
          modelRef.instance.photoForUpload = event.target.files[0];
        });
    }
  }

  public onLogout(): void {
    this.logout.emit();
    this.close();
  }

  public onChangePassword(): void {
    this.changePassword.emit();
    this.close();
  }

  public close(): void {
    this.open = false;
    this.openChange.emit(false);
  }

  public onDrawerOpenChange(open: boolean): void {
    this.open = open;
    this.openChange.emit(open);
    if (!open) {
      this.resetForm();
    }
  }

  /** Restore every field to the value it had when the drawer was opened. */
  private resetForm(): void {
    if (this.pristineValue) {
      this.form.reset(this.pristineValue, {emitEvent: false});
    }
  }

  /** True when the user changed at least one preference (enables Save). */
  public hasChanges(): boolean {
    if (!this.pristineValue || this.form.invalid) {
      return false;
    }
    const current = this.form.getRawValue();
    return Object.keys(this.pristineValue).some(key => current[key] !== this.pristineValue[key]);
  }

  public save(): void {
    this.saving = true;
    this.updateSettings()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: () => {
          this.saving = false;
          // The saved values become the new baseline.
          this.pristineValue = this.form.getRawValue();
          this.form.markAsPristine();
          this.close();
        },
        error: () => this.saving = false
      });
  }

  private updateSettings(): Observable<any> {
    const current: ProfilePreferences = this.form.getRawValue();
    const data: {
      id: number,
      timezone?: string,
      locale?: string,
      dateFormat?: string,
      integerMask?: string,
      decimalMask?: string,
      autoSave?: boolean,
      sendEmail?: boolean,
      email?: string
    } = {
      id: this.user.id
    };

    let updateUserDataObs: Observable<any> = this.userService.updateUserData(data);

    if (current.dateFormat !== this.pristineValue.dateFormat) {
      data.dateFormat = current.dateFormat;
      updateUserDataObs = updateUserDataObs.pipe(tap(() => this.user.dateFormat = current.dateFormat));
    }

    if (current.numberFormat !== this.pristineValue.numberFormat) {
      // numberFormat is `decimal + separator`.
      data.integerMask = current.numberFormat[1];
      data.decimalMask = current.numberFormat[0];
      updateUserDataObs = updateUserDataObs.pipe(tap(() => {
        this.user.integerMask = current.numberFormat[1];
        this.user.decimalMask = current.numberFormat[0];
      }));
    }

    if (current.timezone !== this.pristineValue.timezone) {
      data.timezone = current.timezone;
      updateUserDataObs = updateUserDataObs.pipe(tap(() => this.user.timezone = current.timezone));
    }

    if (current.language !== this.pristineValue.language) {
      data.locale = current.language;
      updateUserDataObs = updateUserDataObs.pipe(tap(() => this.user.language = current.language));
    }

    if (current.email !== this.pristineValue.email) {
      data.email = current.email;
      updateUserDataObs = updateUserDataObs.pipe(tap(() => this.user.email = current.email));
    }

    if (current.sendEmail !== this.pristineValue.sendEmail) {
      data.sendEmail = current.sendEmail;
      updateUserDataObs = updateUserDataObs.pipe(tap(() => this.user.sendEmail = current.sendEmail));
    }

    if (current.autoSave !== this.pristineValue.autoSave) {
      data.autoSave = current.autoSave;
      updateUserDataObs = updateUserDataObs.pipe(tap(() => this.user.autoSave = current.autoSave));
    }

    return updateUserDataObs.pipe(tap(() => this.userService.userSubject.next(this.user)));
  }
}

/** Shape of the reactive preferences form. */
interface ProfilePreferences {
  language: string;
  timezone: string;
  numberFormat: string;
  dateFormat: string;
  email: string;
  sendEmail: boolean;
  autoSave: boolean;
}






